import { Patient } from '../types/patient';
import { johnSmithAppleHealthData } from './appleHealthData';
import {johnSmithHealthPlan} from './MockHealthPlans'

export const mockPatients: Patient[] = [
  {
    id: '1',
    firstName: 'Michael',
    lastName: 'Smith',
    dateOfBirth: '1965-03-15',
    mrn: 'MRN001234',
    lastVisit: '2024-01-15',
    riskLevel: 'medium',
    healthMetrics: {
      obesityAnalysis: {
        bmi: 28.5,
        vo2Max: 32,
        bodyFatPercentage: 26,
        muscleMassIndex: 32,
        risk: 'medium'
      },
      metabolicAnalysis: {
        insulinLevel: 18,
        a1c: 6.2,
        glucose: 110,
        creatinine: 1.1,
        egfr: 85,
        risk: 'medium'
      },
      cholesterolAnalysis: {
        apoB: 120,
        ldl: 140,
        hdl: 35,
        total: 220,
        risk: 'high'
      },
      cardiovascularAnalysis: {
        systolic: 145,
        diastolic: 92,
        heartRate: 78,
        respiratoryRate: 16,
        risk: 'high'
      },
      metabolicSyndrome: {
        risk: 'medium',
        score: 3,
        factors: ['Elevated blood pressure', 'High triglycerides', 'Abdominal obesity']
      },
      hypertension: {
        systolic: 145,
        diastolic: 92,
        risk: 'high'
      },
      liverEnzymes: {
        alt: 45,
        ast: 38,
        risk: 'low'
      },
      obesity: {
        bmi: 28.5,
        risk: 'medium'
      },
      heartDisease: {
        cholesterol: {
          total: 220,
          ldl: 140,
          hdl: 35
        },
        risk: 'medium'
      }
    },
    wellbeingFactors: {
      mentalHealth: {
        depression: { score: 8, comment: "Feeling pretty good overall, just some stress from work deadlines." },
        anxiety: { score: 5, comment: "Sometimes feel anxious about health issues, especially blood sugar levels." },
        stress: { score: 3, comment: "Work has been very demanding lately, struggling to find time to relax." }
      },
      socialFactors: {
        support: { score: 6, comment: "Family is supportive, but don't see them as often as I'd like." },
        isolation: { score: 8, comment: "Have good connections with coworkers and neighbors." }
      },
      lifestyle: {
        exercise: { score: 3, comment: "Hard to find time for exercise with my schedule." },
        nutrition: { score: 5, comment: "Trying to eat better but slip up on busy days." },
        sleep: { score: 4, comment: "Often wake up during the night, especially when stressed." },
        smoking: { score: 10, comment: "Never smoked, not interested in starting." },
        alcohol: { score: 6, comment: "Occasional wine with dinner on weekends." }
      },
      environmental: {
        foodInsecurity: { score: 9, comment: "No concerns about food access." },
        financialStress: { score: 5, comment: "Medical bills are adding up, but managing okay." },
        housing: { score: 9, comment: "Have a stable apartment with a good landlord." }
      }
    },
    labResults: [
      // Most recent values (January 2025)
      {
        id: '1',
        name: 'Hemoglobin A1c',
        value: 6.2,
        unit: '%',
        referenceRange: '< 5.7%',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '2',
        name: 'Total Cholesterol',
        value: 220,
        unit: 'mg/dL',
        referenceRange: '< 200 mg/dL',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '3',
        name: 'LDL Cholesterol',
        value: 140,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '4',
        name: 'HDL Cholesterol',
        value: 35,
        unit: 'mg/dL',
        referenceRange: '> 40 mg/dL',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '5',
        name: 'Glucose',
        value: 110,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '6',
        name: 'Creatinine',
        value: 1.1,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2025-01-10'
      },
      {
        id: '7',
        name: 'eGFR',
        value: 85,
        unit: 'mL/min',
        referenceRange: '> 60 mL/min',
        status: 'normal',
        date: '2025-01-10'
      },
      {
        id: '8',
        name: 'Apolipoprotein B',
        value: 120,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2025-01-10'
      },
      {
        id: '9',
        name: 'BMI',
        value: 28.5,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2025-01-10'
      },
      // December 2024
      {
        id: '10',
        name: 'Glucose',
        value: 108,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'abnormal',
        date: '2024-12-05'
      },
      {
        id: '11',
        name: 'LDL Cholesterol',
        value: 145,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2024-12-05'
      },
      {
        id: '12',
        name: 'Creatinine',
        value: 1.05,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2024-12-05'
      },
      {
        id: '13',
        name: 'BMI',
        value: 28.8,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2024-12-05'
      },
      // November 2024
      {
        id: '14',
        name: 'Glucose',
        value: 105,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'abnormal',
        date: '2024-11-02'
      },
      {
        id: '15',
        name: 'LDL Cholesterol',
        value: 148,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2024-11-02'
      },
      {
        id: '16',
        name: 'Creatinine',
        value: 1.08,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2024-11-02'
      },
      {
        id: '17',
        name: 'BMI',
        value: 29.1,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2024-11-02'
      },
      // October 2024
      {
        id: '18',
        name: 'Glucose',
        value: 102,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'abnormal',
        date: '2024-10-10'
      },
      {
        id: '19',
        name: 'LDL Cholesterol',
        value: 150,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2024-10-10'
      },
      {
        id: '20',
        name: 'Creatinine',
        value: 1.12,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2024-10-10'
      },
      {
        id: '21',
        name: 'BMI',
        value: 29.3,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2024-10-10'
      },
      // September 2024
      {
        id: '22',
        name: 'Glucose',
        value: 98,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'normal',
        date: '2024-09-15'
      },
      {
        id: '23',
        name: 'LDL Cholesterol',
        value: 152,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2024-09-15'
      },
      {
        id: '24',
        name: 'Creatinine',
        value: 1.1,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2024-09-15'
      },
      {
        id: '25',
        name: 'BMI',
        value: 29.5,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2024-09-15'
      },
      // August 2024
      {
        id: '26',
        name: 'Glucose',
        value: 95,
        unit: 'mg/dL',
        referenceRange: '70-100 mg/dL',
        status: 'normal',
        date: '2024-08-20'
      },
      {
        id: '27',
        name: 'LDL Cholesterol',
        value: 155,
        unit: 'mg/dL',
        referenceRange: '< 100 mg/dL',
        status: 'abnormal',
        date: '2024-08-20'
      },
      {
        id: '28',
        name: 'Creatinine',
        value: 1.15,
        unit: 'mg/dL',
        referenceRange: '0.6-1.2 mg/dL',
        status: 'normal',
        date: '2024-08-20'
      },
      {
        id: '29',
        name: 'BMI',
        value: 29.8,
        unit: 'kg/m²',
        referenceRange: '18.5-24.9',
        status: 'abnormal',
        date: '2024-08-20'
      }
    ],
    medications: [
      {
        id: '1',
        name: 'Lisinopril',
        dosage: '10mg',
        code: 'RXN314076',
        datePairs: [{
          prescribedDate: '2023-12-01',
          status: 'active'
        }],
        overallStatus: 'active',
        totalPrescriptions: 1,
        totalDeliveries: 0
      }
    ],
    allergies: [
      {
        id: '1',
        allergen: 'Penicillin',
        severity: 'moderate',
        reaction: 'Rash'
      }
    ],
    appleHealthData: johnSmithAppleHealthData,
    plans: johnSmithHealthPlan,
    actionPlans: [
      {
        id: 'sleep-quality-ms',
        patientId: '1',
        name: 'Sleep Quality Improvement',
        description: 'Enhance sleep duration and quality through better sleep hygiene and consistent routines.',
        icon: 'Moon',
        iconColor: 'bg-info-100 text-info-600',
        status: 'in-progress',
        progress: 45,
        goals: [
          'Sleep 7-8 hours nightly',
          'Establish consistent sleep schedule',
          'Reduce screen time before bed',
          'Create relaxing bedtime routine',
        ],
        timeline: '2 months',
        startDate: '2024-11-01',
        metrics: [
          { label: 'Avg Sleep Duration', current: '6.5 hrs', target: '7.5 hrs' },
          { label: 'Sleep Quality Score', current: '68/100', target: '80/100' },
          { label: 'Wake Episodes', current: '2.5', target: '<2' },
        ],
      },
      {
        id: 'weight-loss-ms',
        patientId: '1',
        name: 'Weight Loss Plan',
        description: 'Achieve and maintain a healthy weight through balanced nutrition and regular physical activity.',
        icon: 'Activity',
        iconColor: 'bg-success-100 text-success-600',
        status: 'active',
        progress: 65,
        goals: [
          'Reduce weight by 15 lbs',
          'Exercise 150 minutes per week',
          'Track daily caloric intake',
          'Maintain balanced macronutrients',
        ],
        timeline: '3 months',
        startDate: '2024-10-15',
        metrics: [
          { label: 'Weight', current: '185 lbs', target: '170 lbs' },
          { label: 'Weekly Exercise', current: '120 min', target: '150 min' },
          { label: 'Daily Calories', current: '1,850', target: '1,800' },
        ],
      }
    ],
    medicalTimeline: {
      '2024-01-15': {
        content: 'Annual physical examination with Dr. Anderson. Blood pressure elevated at 145/92. Discussed lifestyle modifications and medication adherence.',
        tags: [
          { label: 'Doctor Visit', type: 'event', icon: 'Stethoscope', color: 'blue' },
          { label: 'Cardiovascular', type: 'category', icon: 'Heart', color: 'red' }
        ]
      },
      '2024-01-10': {
        content: 'Comprehensive metabolic panel and lipid panel completed. Results show elevated LDL cholesterol (140 mg/dL) and borderline A1c (6.2%).',
        tags: [
          { label: 'Lab Result', type: 'event', icon: 'TestTube', color: 'purple' },
          { label: 'Metabolic', type: 'category', icon: 'Droplet', color: 'orange' }
        ]
      },
      '2023-12-01': {
        content: 'Started on Lisinopril 10mg daily for hypertension management. Patient counseled on medication timing and potential side effects.',
        tags: [
          { label: 'Medication', type: 'event', icon: 'Pill', color: 'green' },
          { label: 'Cardiovascular', type: 'category', icon: 'Heart', color: 'red' }
        ]
      },
      '2023-10-22': {
        content: 'Follow-up cardiology consultation. Stress test performed with normal results. Continue current management plan.',
        tags: [
          { label: 'Consultation', type: 'event', icon: 'Stethoscope', color: 'blue' },
          { label: 'Cardiovascular', type: 'category', icon: 'Heart', color: 'red' }
        ]
      },
      '2023-08-15': {
        content: 'Routine colonoscopy screening completed. No polyps detected. Recommended follow-up in 10 years.',
        tags: [
          { label: 'Procedure', type: 'event', icon: 'Activity', color: 'indigo' },
          { label: 'Gastroenterology', type: 'category', icon: 'Activity', color: 'yellow' }
        ]
      },
      '2023-06-10': {
        content: 'Eye examination for diabetic screening. No retinopathy detected. Continue annual monitoring.',
        tags: [
          { label: 'Screening', type: 'event', icon: 'Eye', color: 'cyan' },
          { label: 'Ophthalmology', type: 'category', icon: 'Eye', color: 'teal' }
        ]
      }
    }
  }
];
